import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, X, Gauge, Clock, CalendarOff } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useLanguage } from '../context/LanguageContext';

const WEEKDAYS = { el: ['Δευ', 'Τρι', 'Τετ', 'Πεμ', 'Παρ', 'Σαβ', 'Κυρ'], en: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] };

const Section = ({ icon: Icon, title, children }) => (
    <div className="fleet-card p-5 space-y-4">
        <div className="flex items-center gap-2"><Icon className="w-5 h-5 text-teal-700" /><h2 className="text-base md:text-lg font-semibold text-slate-800">{title}</h2></div>
        {children}
    </div>
);

export default function WorkshopSettingsPage() {
    const { language } = useLanguage();
    const el = language === 'el';
    const [form, setForm] = useState(null);
    const [newDate, setNewDate] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => { api.get('/workshop/settings').then((r) => setForm((f) => f || r.data)).catch(() => {}); }, []);
    if (!form) return <Loader2 className="w-6 h-6 animate-spin text-teal-700" />;
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const toggleDay = (i) => set('closed_weekdays', form.closed_weekdays.includes(i) ? form.closed_weekdays.filter((d) => d !== i) : [...form.closed_weekdays, i]);
    const addDate = () => { if (newDate && !form.closed_dates.includes(newDate)) set('closed_dates', [...form.closed_dates, newDate].sort()); setNewDate(''); };

    const save = async () => {
        setSaving(true);
        try {
            const r = await api.put('/workshop/settings', { ...form, daily_capacity: form.daily_capacity ? Number(form.daily_capacity) : null });
            setForm(r.data);
            toast.success(el ? 'Οι ρυθμίσεις αποθηκεύτηκαν' : 'Settings saved');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-6 max-w-2xl" data-testid="workshop-settings-page">
            <div>
                <h1 className="text-2xl font-bold text-slate-800">{el ? 'Ρυθμίσεις συνεργείου' : 'Workshop settings'}</h1>
                <p className="text-zinc-500">{el ? 'Οι υπεύθυνοι στόλου βλέπουν τις γεμάτες και κλειστές μέρες πριν κλείσουν ραντεβού' : 'Fleet managers see full and closed days before booking'}</p>
            </div>
            <Section icon={Gauge} title={el ? 'Χωρητικότητα' : 'Capacity'}>
                <div className="space-y-1 max-w-xs">
                    <Label>{el ? 'Αυτοκίνητα ανά ημέρα' : 'Cars per day'}</Label>
                    <Input type="number" min="1" value={form.daily_capacity ?? ''} onChange={(e) => set('daily_capacity', e.target.value)} placeholder={el ? 'Χωρίς όριο' : 'No limit'} data-testid="settings-capacity" />
                </div>
            </Section>
            <Section icon={Clock} title={el ? 'Ωράριο' : 'Opening hours'}>
                <div className="grid grid-cols-2 gap-3 max-w-sm">
                    <div className="space-y-1"><Label>{el ? 'Άνοιγμα' : 'Opens'}</Label><Input type="time" value={form.open_time} onChange={(e) => set('open_time', e.target.value)} data-testid="settings-open-time" /></div>
                    <div className="space-y-1"><Label>{el ? 'Κλείσιμο' : 'Closes'}</Label><Input type="time" value={form.close_time} onChange={(e) => set('close_time', e.target.value)} data-testid="settings-close-time" /></div>
                </div>
                <div className="space-y-2">
                    <Label>{el ? 'Κλειστές ημέρες εβδομάδας' : 'Closed weekdays'}</Label>
                    <div className="flex flex-wrap gap-2">
                        {WEEKDAYS[el ? 'el' : 'en'].map((d, i) => (
                            <button key={d} type="button" onClick={() => toggleDay(i)} className={`w-14 py-1.5 rounded-sm text-sm transition-colors ${form.closed_weekdays.includes(i) ? 'bg-slate-700 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`} data-testid={`settings-weekday-${i}`}>{d}</button>
                        ))}
                    </div>
                </div>
            </Section>
            <Section icon={CalendarOff} title={el ? 'Αργίες / κλειστές ημερομηνίες' : 'Holidays / closed dates'}>
                <div className="flex gap-2 max-w-sm">
                    <Input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} data-testid="settings-closed-date-input" />
                    <Button variant="outline" onClick={addDate} disabled={!newDate} data-testid="settings-closed-date-add">{el ? 'Προσθήκη' : 'Add'}</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                    {form.closed_dates.map((d) => (
                        <span key={d} className="flex items-center gap-1 text-sm bg-slate-100 px-2 py-1 rounded-sm" data-testid={`settings-closed-date-${d}`}>
                            {d.split('-').reverse().join('/')}
                            <button onClick={() => set('closed_dates', form.closed_dates.filter((x) => x !== d))} className="text-slate-400 hover:text-red-500"><X className="w-3.5 h-3.5" /></button>
                        </span>
                    ))}
                    {!form.closed_dates.length && <p className="text-sm text-slate-400">{el ? 'Καμία' : 'None'}</p>}
                </div>
            </Section>
            <Button onClick={save} disabled={saving} className="bg-teal-700 hover:bg-teal-800 text-white rounded-sm" data-testid="settings-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (el ? 'Αποθήκευση ρυθμίσεων' : 'Save settings')}
            </Button>
        </div>
    );
}
